<template>
    <b-modal
        v-model="show"
        cancel-title="Abbrechen"
        hide-footer
        modal-class="email-damage-record-assign-modal"
        size="lg"
        :title="modalTitle"
        @hidden="resetModalState">
        <div v-if="isLoadingAssignments" class="email-damage-record-assign-modal__loading">
            <csp-loading-indicator/>
        </div>
        <div v-else>
            <div class="d-flex align-items-end mb-3">
                <AppraisalAutocompleteInput
                    :email-id="emailId"
                    @selected="onAppraisalSelected"/>
                <b-button
                    class="ml-2"
                    :disabled="!selectedAppraisal || isAssigning"
                    variant="primary"
                    @click="assign">
                    Verknüpfen
                </b-button>
            </div>

            <div v-if="assignments.length === 0" class="text-muted">
                Dieser E-Mail ist noch kein Gutachten zugeordnet.
            </div>
            <b-table
                v-else
                borderless
                :fields="tableFields"
                :items="tableItems"
                responsive
                striped>
                <template v-slot:table-colgroup="scope">
                    <col
                        v-for="field in scope.fields"
                        :key="field.key"
                        :style="{ width: field.width ?? 'auto' }">
                </template>
                <template v-slot:cell(actions)="row">
                    <div class="table-actions d-flex">
                        <a :href="getAppraisalLink(
                               row.item.vehicleDamageRecordId,
                               row.item.damageRecordAppraisalId
                           )"
                            title="Gutachten öffnen">
                            <i class="inticon-autoclaim_detail"/>
                        </a>
                        <a href="#" title="Zuordnung entfernen" @click.prevent="removeAssignment(row.item.assignment)">
                            <i class="inticon-autoclaim_loschen"/>
                        </a>
                    </div>
                </template>
            </b-table>
        </div>
    </b-modal>
</template>

<script>
    import Container from '@services/Container';
    import AppraisalAutocompleteInput from './AppraisalAutocompleteInput.vue';

    const container = Container.getInstance();
    const digitalMailboxRepository = container.getDigitalMailboxRepository();
    const flashMessenger = container.getFlashMessenger();

    export default {
        name: 'EmailDamageRecordAssignModal',
        components: {AppraisalAutocompleteInput},
        props: {
            emailId: {
                default: null,
                type: String,
            },
            subject: {
                type: String,
                default: '',
            },
            showModal: {
                type: Boolean,
                default: false,
            },
        },
        emits: ['assignments-changed', 'update:showModal'],
        data() {
            return {
                assignments: [],
                hasChanges: false,
                isAssigning: false,
                isLoadingAssignments: false,
                selectedAppraisal: null,
                tableFields: [
                    {key: 'appraisalNumber', label: 'Gutachten'},
                    {key: 'referenceNumber', label: 'Akte'},
                    {key: 'actions', label: 'Aktionen', width: '0px'}, // scales to minimum width
                ],
            };
        },
        computed: {
            modalTitle() {
                return this.subject ? `Gutachten zuordnen – ${this.subject}` : 'Gutachten zuordnen';
            },
            show: {
                get() {
                    return this.showModal;
                },
                set(value) {
                    this.$emit('update:showModal', value);
                },
            },
            tableItems() {
                return this.assignments.map(assignment => ({
                    appraisalNumber: assignment.attributes.appraisalNumber,
                    assignment,
                    referenceNumber: assignment.attributes.referenceNumber,
                    vehicleDamageRecordId: assignment.attributes.vehicleDamageRecordId,
                    damageRecordAppraisalId: assignment.attributes.damageRecordAppraisalId,
                }));
            },
        },
        watch: {
            showModal(newVal) {
                if (newVal) {
                    this.loadAssignments();
                }
            },
        },
        methods: {
            assign() {
                this.isAssigning = true;
                digitalMailboxRepository.assignDamageRecord(this.emailId, this.selectedAppraisal.id)
                    .then(() => {
                        flashMessenger.addSuccess('Gutachten wurde erfolgreich zugeordnet.');
                        this.hasChanges = true;
                        this.resetSearchState();
                        this.loadAssignments();
                    })
                    .finally(() => this.isAssigning = false);
            },
            loadAssignments() {
                this.isLoadingAssignments = true;
                digitalMailboxRepository.loadDamageRecordAssignments(this.emailId)
                    .then((response) => {
                        this.assignments = response.data ?? [];
                    })
                    .finally(() => this.isLoadingAssignments = false);
            },
            onAppraisalSelected(raw) {
                this.selectedAppraisal = raw;
            },
            getAppraisalLink(recordId, damageRecordAppraisalId) {
                return '/de/Schaden-Manager/Akte/Detail/vehicleDamageRecordId/'
                    + recordId
                    + '/cost-units/appraisals/'
                    + damageRecordAppraisalId;
            },
            removeAssignment(assignment) {
                if (!confirm('Wollen Sie diese Zuordnung wirklich entfernen?')) {
                    return;
                }
                digitalMailboxRepository.removeDamageRecordAssignment(this.emailId, assignment.id).then(() => {
                    flashMessenger.addSuccess('Zuordnung wurde entfernt.');
                    this.hasChanges = true;
                    this.loadAssignments();
                });
            },
            resetModalState() {
                if (this.hasChanges) {
                    this.$emit('assignments-changed');
                }
                this.assignments = [];
                this.hasChanges = false;
                this.resetSearchState();
            },
            resetSearchState() {
                this.selectedAppraisal = null;
            },
        },
    }
</script>
